import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {
  type PluginName,
  getName,
  getPluginName,
  type Qualified,
  typeName,
  namesEqual,
  qualify,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {
  type NormalizedAtomConfig,
  type Type,
  Component,
  Plugins,
  Types,
  Expressions,
} from '@packages/systems/wfdl';
import {reportBug} from '@packages/utilities/reporter';
import {
  getDisplayNameForType,
  getPageComponentFromComponentMap,
} from '@packages/utilities/platform';
import {findElementInComponentTree} from '@packages/utilities/core-elements';
import {
  isStringElement,
  isLegacyFreeModeIgnoringConstraints,
} from '@packages/utilities/elementTypeUtils';
import {
  emptyArray,
  optionToArray,
  flatMap,
  zipCat,
  concat,
  identity,
} from '@packages/utilities/fp/utils';
import {entries} from '@packages/utilities/key-map';
import {weakMemo, weakMemoMulti} from '@packages/utilities/memo';
import {either, Err, Ok, type Result} from '@packages/utilities/fp/result';
import {type Option, Some, None} from '@packages/utilities/fp/option';

import * as DesignerErrorCodes from '@packages/systems/core/constants/ErrorCodes';
import type {
  ErrorCode,
  RelationshipConstraintErrorDetails,
} from '@packages/systems/core/constants/ErrorCodes';

import {
  pageConstraintMatches,
  relationshipConstraintMatches,
  hasParentOrWrapsRelationship,
  hasChildrenRelationship,
  hasAncestorsOrDescendantsRelationship,
} from './ConstraintUtils';
import type {
  RelationshipConstraint,
  NormalizedConstraints,
} from '@packages/systems/constraints-types';

import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {getElementBindingContext} from '@packages/systems/dynamo/gateway';
import {EMPTY_INSTANCE_STACK, InstanceStack, push} from './InstanceStack';

const {__DEPRECATED__getAtoms} = Plugins;

type ConstraintCount = {
  constraint: RelationshipConstraint;
  count: number;
  onDisplayName: string;
  contextVisited: boolean;
  againstDisplayName: string;
};

type WrapState = {
  state: 'Valid' | 'Invalid' | 'Init';
  on: NormalizedAtomConfig;
  against: NormalizedAtomConfig[];
};

type ConstraintsWithCount = {
  ancestorConstraintsWithCount: ConstraintCount[];
  descendantConstraintsWithCount: ConstraintCount[];
  wraps: WrapState[];
  childrenConstraintsWithCount: ConstraintCount[];
  parentContraintsWithCount: ConstraintCount[];
  currentParentContraintsWithCount: ConstraintCount[];
};

type RelationshipConstraints = {
  children: ConstraintCount[];
  descendants: ConstraintCount[];
  parent: ConstraintCount[];
  ancestors: ConstraintCount[];
};

type ConstraintTraversalOptions = {
  expandComponentInstances: boolean;
};

const expandedConstraintTraversal: ConstraintTraversalOptions = {
  expandComponentInstances: true,
};

const componentDefinitionConstraintTraversal: ConstraintTraversalOptions = {
  expandComponentInstances: false,
};

/**
 * `isLeaf` allows you to check if a given Atom accepts children.
 */
export const isLeaf = (
  atom: NormalizedAtomConfig,
  plugins: Plugins.PluginsType
) => isLeafType(atom.dataType, plugins);

export const isLeafType = (dataType: Type, plugins: Plugins.PluginsType) =>
  !typeHasElementsOrRichText(plugins)(dataType);

const setCount =
  (count: number) =>
  (cc: ConstraintCount): ConstraintCount =>
    count === cc.count ? cc : {...cc, count};

const zipCatCounts: (
  arg1: Array<Array<ConstraintCount>>
) => Array<ConstraintCount> = zipCat(
  (a) => (b) => setCount(a.count + b.count)(a)
);

const getAncestorConstraintsWithCount = flatMap(
  ({
    ancestorConstraintsWithCount,
  }: {
    ancestorConstraintsWithCount: ConstraintCount[];
  }) => ancestorConstraintsWithCount
);

const getParentContraintsWithCount = flatMap(
  ({
    parentContraintsWithCount,
  }: {
    parentContraintsWithCount: ConstraintCount[];
  }) => parentContraintsWithCount
);

const getCurrentParentContraintsWithCount = flatMap(
  ({
    currentParentContraintsWithCount,
  }: {
    currentParentContraintsWithCount: ConstraintCount[];
  }) => currentParentContraintsWithCount
);

const getWraps = flatMap(({wraps}: ConstraintsWithCount) => wraps);

const getDescendantConstraintsWithCount = ({
  descendantConstraintsWithCount,
}: ConstraintsWithCount) => descendantConstraintsWithCount;

const getChildrenConstraintsWithCount = ({
  childrenConstraintsWithCount,
}: ConstraintsWithCount) => childrenConstraintsWithCount;

const forwardConstraints =
  (descendantConstraints: ConstraintCount[]) =>
  (childrenConstraints: ConstraintCount[]): ConstraintsWithCount => ({
    ancestorConstraintsWithCount: emptyArray,
    descendantConstraintsWithCount: descendantConstraints,
    wraps: emptyArray,
    childrenConstraintsWithCount: emptyArray,
    parentContraintsWithCount: emptyArray,
    currentParentContraintsWithCount: childrenConstraints,
  });

const maybeGetWrapConstraint =
  (plugins: Plugins.PluginsType) =>
  (atom: NormalizedAtomConfig): Option<WrapState> =>
    atom.constraints.wraps.length > 0
      ? // @ts-expect-error - TS2322 - Type 'Option<{ on: NormalizedAtomConfig; against: (NormalizedAtomConfig | undefined)[]; state: "Init"; }>' is not assignable to type 'Option<WrapState>'.
        Some({
          on: atom,
          against: atom.constraints.wraps.map(
            (type) => __DEPRECATED__getAtoms(plugins)[type]
          ),
          state: 'Init',
        })
      : None;

const LeafConstraints = [
  {
    is: {check: () => true, name: 'Element'},
    rule: 'Forbid',
  } as RelationshipConstraint,
];

const setContextVisited = <
  a extends {
    contextVisited: boolean;
  },
>(
  constraintWithCount: a
): a => ({
  ...constraintWithCount,
  contextVisited: true,
});

const serializeForErrorCode: (
  arg1: ConstraintCount
) => RelationshipConstraintErrorDetails = (constraintWithCount) => {
  return {
    on: {
      displayName: constraintWithCount.onDisplayName,
    },
    against: {
      displayName: constraintWithCount.againstDisplayName,
      count: constraintWithCount.count,
    },
    constraint: constraintWithCount.constraint,
  };
};

const convertConstraintsWithErrorCodes =
  ({
    exactly,
    forbid,
    atLeastOne,
    zeroOrOne,
    atMostN,
  }: {
    exactly: (details: RelationshipConstraintErrorDetails) => ErrorCode;
    forbid: (details: RelationshipConstraintErrorDetails) => ErrorCode;
    atLeastOne: (details: RelationshipConstraintErrorDetails) => ErrorCode;
    zeroOrOne: (details: RelationshipConstraintErrorDetails) => ErrorCode;
    atMostN: (
      details: DesignerErrorCodes.AtMostNConstraintErrorDetails
    ) => ErrorCode;
  }) =>
  (
    errors: Array<any> | Array<ErrorCode | any>,
    constraintWithCount: ConstraintCount
  ) => {
    const details = serializeForErrorCode(constraintWithCount);
    switch (constraintWithCount.constraint.rule) {
      // RequireOnly is an inverse of Forbid so we use the same error code creator.
      case 'RequireOnly': {
        if (constraintWithCount.count !== 0) {
          return [...errors, forbid(details)];
        }
        break;
      }
      case 'Forbid':
        if (constraintWithCount.count !== 0) {
          return [...errors, forbid(details)];
        }
        break;
      case 'AtLeastOne':
        if (constraintWithCount.count === 0) {
          return [...errors, atLeastOne(details)];
        }
        break;
      case 'ExactlyOne':
      case 'BindingContext':
        if (constraintWithCount.count !== 1) {
          return [...errors, exactly(details)];
        }
        break;
      case 'ZeroOrOne':
        if (constraintWithCount.count > 1) {
          return [...errors, zeroOrOne(details)];
        }
        break;
      case 'AtMostN': {
        // Within this case, we know constraint.rule is 'AtMostN'
        const constraint = constraintWithCount.constraint;

        if (constraintWithCount.count > constraint.maxCount) {
          const atMostNDetails: DesignerErrorCodes.AtMostNConstraintErrorDetails =
            {
              ...details,
              constraint,
            };
          return [...errors, atMostN(atMostNDetails)];
        }
        break;
      }
    }
    return errors;
  };

const constraintsWithCountToErrors = weakMemo(
  (constraintsWithCount: ConstraintsWithCount): ErrorCode[] => {
    const ancestors = [
      ...constraintsWithCount.ancestorConstraintsWithCount,
      ...constraintsWithCount.parentContraintsWithCount,
    ].reduce(
      convertConstraintsWithErrorCodes({
        exactly: DesignerErrorCodes.RequiredExactlyAncestor,
        atLeastOne: DesignerErrorCodes.RequiredAncestor,
        forbid: DesignerErrorCodes.ForbidAncestor,
        zeroOrOne: DesignerErrorCodes.RequiredZeroOrOneAncestor,
        atMostN: DesignerErrorCodes.RequiredAtMostNAncestor,
      }),
      emptyArray
    );
    const descendants = [
      ...constraintsWithCount.childrenConstraintsWithCount,
      ...constraintsWithCount.descendantConstraintsWithCount,
    ].reduce(
      convertConstraintsWithErrorCodes({
        exactly: DesignerErrorCodes.RequiredExactlyDescendant,
        atLeastOne: DesignerErrorCodes.RequiredDescendant,
        forbid: DesignerErrorCodes.ForbidDescendant,
        zeroOrOne: DesignerErrorCodes.RequiredZeroOrOneDescendant,
        atMostN: DesignerErrorCodes.RequiredAtMostNDescendant,
      }),
      emptyArray
    );

    const wraps = constraintsWithCount.wraps.reduce((errors, wrap) => {
      switch (wrap.state) {
        case 'Invalid':
          return [...errors, DesignerErrorCodes.OneInstancePerComponent];
        default:
          return errors;
      }
    }, emptyArray);

    return [...descendants, ...ancestors, ...wraps];
  }
);

const toConstraintCount = (
  constraint: RelationshipConstraint,
  againstDisplayName: string,
  onDisplayName: string
): ConstraintCount => {
  return {
    contextVisited: false,
    constraint,
    count: 0,
    onDisplayName,
    againstDisplayName,
  };
};

function getDisplayName(
  plugins: Plugins.PluginsType,
  atomType: string
): string {
  const atoms = __DEPRECATED__getAtoms(plugins);
  return Expressions.getStringFromDisplayName(
    (atoms[atomType] as NormalizedAtomConfig).displayName
  );
}

function getConstraintName(
  plugins: Plugins.PluginsType,
  constraint: RelationshipConstraint
): string {
  // Check for an explicit name property.
  if ('name' in constraint && constraint.name !== undefined) {
    return constraint.name;
  }
  // Check for single constraint.
  else if ('is' in constraint && typeof constraint.is === 'string') {
    return getDisplayName(plugins, constraint.is);
  }
  // Otherwise it's escape hatch.
  else {
    // @ts-expect-error - We expect this error to be thrown if the constraint is not a string.
    return constraint.is.name;
  }
}

const memoizedConcat: <T>(arg1: Array<T>) => (arg1: Array<T>) => Array<T> =
  weakMemo((left) =>
    left.length === 0
      ? identity
      : weakMemo((right) => (right.length === 0 ? left : right.concat(left)))
  );

type ToConstraintCountsFn = (
  constraints: RelationshipConstraint[]
) => Array<ConstraintCount>;

const withCountsAndDisplayName: (
  arg1: Plugins.PluginsType
) => (
  arg1: Component.ComponentMap
) => (arg1: Qualified<Component.ComponentName>) => ToConstraintCountsFn =
  weakMemo((plugins) =>
    weakMemo((componentMap) => {
      /**
       * Instead of building up a cache for all types eagerly, we build one lazily per
       * component map + plugins instance. This is because we don't want to do any work
       * until we are sure a given element type is used.
       *
       * We are unable to use our typical memoization arsenal because element.type's
       * are not always the same reference per a given type.
       */
      let cache: QualifiedMap<Component.ComponentName, ToConstraintCountsFn> =
        QualifiedMap.empty();

      return (type) => {
        const cachedValue = cache.get(type);
        if (cachedValue) {
          return cachedValue;
        }

        const factoryPerType = weakMemo(
          (constraints: RelationshipConstraint[]) => {
            return constraints.map((constraint) => {
              const constraintName = getConstraintName(
                plugins,
                constraint as RelationshipConstraint
              );

              return toConstraintCount(
                constraint,
                constraintName,
                Expressions.getStringFromDisplayName(
                  getDisplayNameForType(type, componentMap, plugins)
                )
              );
            });
          }
        );

        cache = cache.set(type, factoryPerType);

        return factoryPerType;
      };
    })
  );

const groupResults: (arg1: ErrorCode[]) => {
  [key: string]: ErrorCode[];
} = (messages) =>
  messages.reduce<Record<string, any>>((memo, message) => {
    const serialized = DesignerErrorCodes.toString(message);
    memo[serialized] = memo[serialized]
      ? memo[serialized].concat([message])
      : [message];
    return memo;
  }, {});

/**
 * Returns an error if placing an element in the `rawChild` tree on `page`
 * violates its page constraints.
 *
 * The search recurses into any user component instances encountered that have
 * corresponding component definitions in `components`.
 */
export const validatePageConstraints =
  (page: PageRecordType) =>
  (components: Component.ComponentMap) =>
  (rawChild: ExpressionElement, plugins: Plugins.PluginsType) => {
    const doesElementViolatePageConstraints = (element: ExpressionElement) => {
      if (isStringElement(element)) return false;

      const constraints = getConstraintsForElement(
        element,
        components,
        plugins
      );

      const violatesConstraint = constraints.page.some(
        (constraint) =>
          constraint.type !== 'UtilityPage' ||
          !pageConstraintMatches(constraint, page)
      );

      return violatesConstraint;
    };

    const violatingElement = findElementInComponentTree(
      rawChild,
      doesElementViolatePageConstraints,
      components
    );

    if (!violatingElement) return;

    const firstErrorAtom = Plugins.getAtom(plugins, violatingElement);

    const displayName = Expressions.getStringFromDisplayName(
      firstErrorAtom.displayName
    );

    return Err(`${displayName} elements are not allowed in ${page.name} page.`);
  };

/**
 * `gatherRelatedElements` returns a list of elements that are related to each other based on their constraints.
 *
 * Example: If an element has a constraint that requires exactly one parent, its parent would be returned as a related element.
 */
export const gatherRelatedElements = (
  root: Expression,
  potentialRelativeSource: ExpressionElement,
  selectedElement: ExpressionElement,
  liveComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): ExpressionElement | null => {
  // the parent element of the selected eleemnt
  const parentElement = Expressions.getParent(root, selectedElement.id);

  // the parent element has no type (root)
  if (!parentElement) {
    return null;
  }
  const selectedElementConstraints = getConstraintsForElement(
    selectedElement,
    liveComponentMap,
    plugins
  );
  const parentElementConstraints = getConstraintsForElement(
    parentElement,
    liveComponentMap,
    plugins
  );

  // Check whether the selected element's parent has children or wraps
  // constraints that match the selected element type.
  const isParentOrWrapsIncluded = hasParentOrWrapsRelationship(
    liveComponentMap,
    selectedElement,
    parentElementConstraints
  );

  // Check whether the selected element has parent constraints that
  // match the parent element type.
  const isChildrenIncluded = hasChildrenRelationship(
    liveComponentMap,
    parentElement,
    selectedElementConstraints
  );

  // If we've already found parent, wraps, or children relations, return the
  // related element and don't do the `go` walk.
  if (isParentOrWrapsIncluded || isChildrenIncluded) {
    return potentialRelativeSource;
  }

  const go = (
    potentialRelative: ExpressionElement,
    potentialRelativeConstraints: NormalizedConstraints
  ): ExpressionElement | null => {
    // Check whether the selected element has ancestor constraints that match the
    // parent element type.
    const isRelatedAncestor = hasAncestorsOrDescendantsRelationship(
      liveComponentMap,
      potentialRelative,
      selectedElementConstraints,
      'ancestors'
    );

    // Check whether the parent of the selected element has descendant
    // constraints that match the selected element type.
    const isRelatedDescendant = hasAncestorsOrDescendantsRelationship(
      liveComponentMap,
      selectedElement,
      potentialRelativeConstraints,
      'descendants'
    );

    if (isRelatedDescendant || isRelatedAncestor) {
      return potentialRelative;
    } else {
      const nextElement = Expressions.getParent(root, potentialRelative.id);
      if (!nextElement) {
        return null;
      }
      const nextElementConstraints = getConstraintsForElement(
        nextElement,
        liveComponentMap,
        plugins
      );
      return go(nextElement, nextElementConstraints);
    }
  };

  return go(
    potentialRelativeSource,
    getConstraintsForElement(potentialRelativeSource, liveComponentMap, plugins)
  );
};

/**
 * Everything below exercised by test and is intended for production use
 * in the future as part of our platform migrations.
 */

const invokeWrapElementConstraints =
  (plugins: Plugins.PluginsType) =>
  (children: Array<Expression>) =>
  (wrapConstraint: WrapState) => {
    if (wrapConstraint.against.length !== children.length) {
      return {
        ...wrapConstraint,
        state: 'Invalid',
      };
    }

    const orderAndTypesMatch = wrapConstraint.against.every((type, index) => {
      const child = children[index];
      if (!child) return false;
      const element = Expressions.getElement(child);
      if (!element) return false;

      return type === Plugins.getAtomByType(plugins, getName(element.type));
    });
    return orderAndTypesMatch
      ? {
          ...wrapConstraint,
          state: 'Valid',
        }
      : {
          ...wrapConstraint,
          state: 'Invalid',
        };
  };

function buildElementConstraintsInvoker(
  plugins: Plugins.PluginsType,
  collections: CollectionsType,
  currentPage: PageRecordType,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  element: ExpressionElement
) {
  return function invokeElementConstraints(
    constraints: ConstraintCount[]
  ): ConstraintCount[] {
    if (constraints.length === 0) {
      // avoid creating a new empty array unnecessarily
      return constraints;
    }
    return constraints.map((constraintCount: ConstraintCount) => {
      if (constraintCount.contextVisited) return constraintCount;

      const bindingContext =
        constraintCount.constraint.rule === 'BindingContext'
          ? getElementBindingContext(
              element.id,
              currentComponent,
              componentMap,
              currentPage,
              collections
            )
          : null;

      let isMatch = relationshipConstraintMatches({
        constraint: constraintCount.constraint,
        element,
        componentMap,
        bindingContextMatchesExternalKey: bindingContext
          ? (pluginName: PluginName, externalKey: string) =>
              bindingContext.matchesExternalKey(pluginName, externalKey)
          : null,
      });
      // For checking the current binding context, it makes sense to only check
      // the nearest parent's binding context. Once we have checked the
      // nearest parent's binding context, we set a flag in the constraint object
      // so that we can skip all the future calls of the BindingContext constraint in
      // the current constraint validation chain. It's important because even if a
      // higher-level binding context passes the constraint validation, we are always
      // interested only in the nearest binding context because the nearest context
      // always shadows more distant contexts.
      constraintCount =
        constraintCount.constraint.rule === 'BindingContext'
          ? setContextVisited(constraintCount)
          : constraintCount;

      // RequireOnly is an inverse relationship of Forbid.
      if (constraintCount.constraint.rule === 'RequireOnly') {
        isMatch = !isMatch;
      }

      if (Array.isArray(constraintCount.constraint.is)) {
        if (isMatch) {
          const name = Expressions.getStringFromDisplayName(
            getDisplayNameForType(element.type, componentMap, plugins)
          );
          if (constraintCount.againstDisplayName === name) {
            // if the same element type is violating the rule, increment the count
            return setCount(constraintCount.count + 1)(constraintCount);
          } else {
            // if a new element type is violating the rule, set the type as the
            // new display name and reset the count to 1
            constraintCount.againstDisplayName = name;
            return setCount(1)(constraintCount);
          }
        }
        return constraintCount;
      }

      return isMatch
        ? setCount(constraintCount.count + 1)(constraintCount)
        : constraintCount;
    });
  };
}

export function getConstraintsForElement(
  element: ExpressionElement,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): NormalizedConstraints {
  const componentRoot = componentMap.get(element.type);
  if (componentRoot) {
    return Component.getConstraints(componentRoot);
  }

  return Plugins.getAtom(plugins, element).constraints;
}

function getDataTypeForElementByType(
  type: Qualified<Component.ComponentName>,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Type {
  const componentRoot = componentMap.get(type);
  if (componentRoot) {
    return Component.getDataType(componentRoot);
  }
  return Plugins.getAtomByType(plugins, getName(type)).dataType;
}

const RICH_TEXT_CHILDREN_TYPE = qualify(
  Plugins.name('Basic'),
  typeName('RichTextChildren')
);

const typeHasElementsOrRichText = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo(
    Types.anyType((dataType: Type): boolean => {
      const tyName = Types.getTypeConstructor(dataType);
      // If we meet Aliases we need to unwrap them to check if the underlying
      // type has elements or rich text
      const aliasType = Types.getAliasType(dataType, plugins);
      if (aliasType) {
        return typeHasElementsOrRichText(plugins)(aliasType);
      }
      return (
        Types.isElementType(dataType) ||
        Boolean(tyName && namesEqual(tyName, RICH_TEXT_CHILDREN_TYPE))
      );
    })
  )
);

const getConstraintsWithElement: (
  arg1: Plugins.PluginsType
) => (
  arg1: Component.ComponentMap
) => (arg1: ExpressionElement) => RelationshipConstraints = weakMemo(
  (plugins) =>
    weakMemo((componentMap) => {
      let cache: QualifiedMap<
        Component.ComponentName,
        RelationshipConstraints
      > = QualifiedMap.empty();

      return (element) => {
        const type = element.type;
        const cached = cache.get(type);
        if (cached) {
          return cached;
        }

        const dataType = getDataTypeForElementByType(
          type,
          componentMap,
          plugins
        );
        const constraints = getConstraintsForElement(
          element,
          componentMap,
          plugins
        );

        const constraintsToCountWithTypes =
          withCountsAndDisplayName(plugins)(componentMap)(type);

        const childrenConstraints = constraintsToCountWithTypes(
          constraints.children
        );

        const currentDescendantConstraints = concat(
          isLeafType(dataType, plugins)
            ? constraintsToCountWithTypes(LeafConstraints)
            : emptyArray
        )(constraintsToCountWithTypes(constraints.descendants));

        const parentConstraints = constraintsToCountWithTypes(
          constraints.parent
        );

        const ancestorConstraints = constraintsToCountWithTypes(
          constraints.ancestors
        );

        const elementConstraints: RelationshipConstraints = {
          children: childrenConstraints,
          descendants: currentDescendantConstraints,
          parent: parentConstraints,
          ancestors: ancestorConstraints,
        };

        cache = cache.set(type, elementConstraints);

        return elementConstraints;
      };
    })
);

const childrenPath: Array<{at: string; in: 'Record'}> = [
  {in: 'Record', at: 'children'},
];

/**
 * Returns the expression list at `data.children` from `element` data.
 *
 * If `data.children` is a prop binding, we iterate through `instanceStack` in
 * order to recursively substitute bindings with instance overrides until
 * substitution fails, we encounter a non-list or non-binding override, or
 * we find the ultimate override list value.
 *
 * Performance note: Making these substitutions directly to the component render
 * expression requires rebuilding potentially large expression trees repeatedly.
 * This approach allows us to make the substitution when the data is needed
 * and without needing to rebuild expressions.
 */
function getChildrenWithSubstitution(
  element: ExpressionElement,
  instanceStack: InstanceStack
): null | {
  children: Expression[];
  childInstanceStack: InstanceStack;
} {
  const data = Expressions.getElementData(element);
  let childrenExpression = Expressions.getIn(data, childrenPath);

  if (childrenExpression) {
    const children = Expressions.getList(childrenExpression);
    if (children) {
      return {children, childInstanceStack: instanceStack};
    }

    let stack = instanceStack;

    while (stack.inst !== null) {
      const sel = Expressions.getSelect(childrenExpression);

      if (!sel) {
        // this currently only substitutes direct selects
        break;
      }

      const {inst, tail} = stack;
      const dataRecord = Expressions.getRecord(
        Expressions.getElementData(inst)
      );
      const {prop, from} = sel;

      if (Expressions.isDataVariable(from) && dataRecord && dataRecord[prop]) {
        // Substitute the prop binding with the override from the
        // corresponding instance.
        childrenExpression = dataRecord[prop];
      } else {
        // Bail out if we encountered a select that we cannot substitute
        // with element data from the corresponding component instance!
        // This prevents us from trying to make data substitutions from
        // the wrong scope.
        break;
      }

      // check if we now have children after substitution
      const childrenFromInst = Expressions.getList(childrenExpression);
      if (childrenFromInst) {
        return {
          children: childrenFromInst,
          // children originate from the override of the instance at the
          // head of instanceStack, so the correct stack to use for their
          // evaluation is the tail of that stack
          childInstanceStack: tail,
        };
      }

      // pop the stack for the next iteration
      stack = tail;
    }
  }

  return null;
}

const containsPropBindings = Expressions.some(Expressions.isDataVariable);

/**
 * If this function is provided a component instance, it will return the
 * component's render body and the stack of instances that had to be expanded
 * to arrive at that render from the input expression.
 *
 * const Component = ({children}) => <a>{children}</a>
 * const instance = <Component><a>hello</a></Component>
 *
 * const result = maybeSubstituteElementWithComponent(instance, Component, componentMap);
 * // result === {child: <a>{children}</a>, instances: [<Component><a>hello</a></Component>]}
 *
 * The ComponentMap is provided to allow for recursive substitution.
 */
function maybeSubstituteElementWithComponent(
  element: Expression,
  component: Component.ComponentType,
  componentMap: Component.ComponentMap,
  instanceStack: InstanceStack
): {
  child: Expression;
  component: Component.ComponentType;
  instanceStack: InstanceStack;
} {
  const maybeInstanceElement = Expressions.getElement(element);
  const nextComponent: Component.ComponentType | null | undefined =
    maybeInstanceElement ? componentMap.get(maybeInstanceElement.type) : null;
  if (maybeInstanceElement && nextComponent) {
    const nextRender = Component.getRender(nextComponent);
    // If the render never reads prop values from data, we supply an empty
    // instance stack to keep instanceStack references as stable as possible
    // for memoization efficiency.
    const nextInstanceStack = containsPropBindings(nextRender)
      ? push(instanceStack)(maybeInstanceElement)
      : EMPTY_INSTANCE_STACK;

    const nextElement = Expressions.getElement(nextRender);
    if (nextElement && componentMap.has(nextElement.type)) {
      return maybeSubstituteElementWithComponent(
        nextRender,
        nextComponent,
        componentMap,
        nextInstanceStack
      );
    }

    // Here we swap out component instances with their root elements
    return {
      child: nextRender,
      component: nextComponent,
      instanceStack: nextInstanceStack,
    };
  }

  return {
    child: element,
    component,
    instanceStack,
  };
}

function getChildrenElementsAndComponentsForConstraintTraversal(
  element: ExpressionElement,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  traversalOptions: ConstraintTraversalOptions,
  instanceStack: InstanceStack
): Array<{
  child: Expression;
  component: Component.ComponentType;
  instanceStack: InstanceStack;
}> {
  if (
    !traversalOptions.expandComponentInstances &&
    componentMap.has(element.type)
  ) {
    return emptyArray;
  }

  const childrenAndStack = getChildrenWithSubstitution(element, instanceStack);

  if (!childrenAndStack || childrenAndStack.children.length === 0) {
    return emptyArray;
  }

  const {children, childInstanceStack} = childrenAndStack;
  return children.map((child) =>
    traversalOptions.expandComponentInstances
      ? maybeSubstituteElementWithComponent(
          child,
          currentComponent,
          componentMap,
          childInstanceStack
        )
      : {child, component: currentComponent, instanceStack: childInstanceStack}
  );
}

const getChildrenElementsAndComponentsForConstraintTraversalMemo =
  weakMemoMulti(getChildrenElementsAndComponentsForConstraintTraversal);

const runComponentValidation = weakMemoMulti(function _runComponentValidation(
  plugins: Plugins.PluginsType,
  collections: CollectionsType,
  currentPage: PageRecordType,
  componentMap: Component.ComponentMap,
  traversalOptions: ConstraintTraversalOptions,
  currentComponent: Component.ComponentType,
  incomingDescendantConstraints: ConstraintCount[],
  incomingChildrenConstraints: ConstraintCount[],
  expression: Expression,
  instanceStack: InstanceStack
): ConstraintsWithCount {
  const element = Expressions.getElement(expression);
  if (
    !element ||
    isStringElement(element) ||
    isLegacyFreeModeIgnoringConstraints(element)
  ) {
    return forwardConstraints(incomingDescendantConstraints)(
      incomingChildrenConstraints
    );
  }

  const invoke = buildElementConstraintsInvoker(
    plugins,
    collections,
    currentPage,
    currentComponent,
    componentMap,
    element
  );

  const constraints = getConstraintsWithElement(plugins)(componentMap)(element);

  const childrenConstraints = constraints.children;
  const currentDescendantConstraints = constraints.descendants;
  const parentConstraints = constraints.parent;
  const ancestorConstraints = constraints.ancestors;
  const catCurrentDescendants = memoizedConcat(currentDescendantConstraints);
  const descendantConstraints = catCurrentDescendants(
    incomingDescendantConstraints
  );

  const childrenAndComponents =
    getChildrenElementsAndComponentsForConstraintTraversalMemo(
      element,
      currentComponent,
      componentMap,
      traversalOptions,
      instanceStack
    );

  const wrapResults: WrapState[] = componentMap.get(element.type)
    ? emptyArray
    : optionToArray(
        maybeGetWrapConstraint(plugins)(
          Plugins.getAtomByType(plugins, getName(element.type))
        ).map((wrapConstraint) => {
          const children = childrenAndComponents.map(({child}) => child);
          return invokeWrapElementConstraints(plugins)(children)(
            wrapConstraint
          );
        })
      );

  const unzippedDescendants = [
    catCurrentDescendants(invoke(incomingDescendantConstraints)),
  ];

  const childAncestors: ConstraintCount[] = [];

  const parents: ConstraintCount[] = [];

  const childWraps: WrapState[] = [];

  const unzippedChildrenConstraints = [childrenConstraints];

  for (const {
    child,
    component,
    instanceStack: childInstanceStack,
  } of childrenAndComponents) {
    const childValidationResult = runComponentValidation(
      plugins,
      collections,
      currentPage,
      componentMap,
      traversalOptions,
      component,
      descendantConstraints,
      childrenConstraints,
      child,
      childInstanceStack
    );

    unzippedDescendants.push(
      getDescendantConstraintsWithCount(childValidationResult)
    );

    childAncestors.push(
      ...invoke(childValidationResult.ancestorConstraintsWithCount)
    );

    parents.push(
      ...invoke(childValidationResult.currentParentContraintsWithCount)
    );
    parents.push(...childValidationResult.parentContraintsWithCount);

    childWraps.push(...childValidationResult.wraps);

    unzippedChildrenConstraints.push(
      getChildrenConstraintsWithCount(childValidationResult)
    );
  }

  const descendants = zipCatCounts(unzippedDescendants);

  const ancestors = concat(ancestorConstraints)(childAncestors);

  const childrenConstraintsResults = invoke(incomingChildrenConstraints).concat(
    zipCatCounts(unzippedChildrenConstraints)
  );

  const wraps = concat(wrapResults)(childWraps);

  return {
    ancestorConstraintsWithCount: ancestors,
    descendantConstraintsWithCount: descendants,
    wraps,
    childrenConstraintsWithCount: childrenConstraintsResults,
    parentContraintsWithCount: parents,
    currentParentContraintsWithCount: parentConstraints,
  };
});

export function validateComponentMap(
  collections: CollectionsType,
  currentPage: PageRecordType | null,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Array<ErrorCode> {
  const pageComponent = getPageComponentFromComponentMap(componentMap);

  if (!pageComponent) {
    reportBug(
      'Missing Page Component',
      'validateComponentMap expects a Page component',
      {
        team: 'core',
        ticket: 'https://webflow.atlassian.net/browse/PLAT-1050',
      }
    );
    return emptyArray;
  }

  if (!currentPage) {
    throw new Error('Missing currentPage in validateComponentMap');
  }

  const context = runComponentValidation(
    plugins,
    collections,
    currentPage,
    componentMap,
    expandedConstraintTraversal,
    pageComponent,
    emptyArray,
    emptyArray,
    Component.getRender(pageComponent),
    EMPTY_INSTANCE_STACK
  );
  return constraintsWithCountToErrors(context);
}

function validateComponentDefinitionConstraints(
  collections: CollectionsType,
  currentPage: PageRecordType,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  currentComponent: Component.ComponentType,
  expression: Expression
): ErrorCode[] {
  const context = runComponentValidation(
    plugins,
    collections,
    currentPage,
    componentMap,
    componentDefinitionConstraintTraversal,
    currentComponent,
    emptyArray,
    emptyArray,
    expression,
    EMPTY_INSTANCE_STACK
  );
  return constraintsWithCountToErrors(context);
}

const validateComponentMapsAndGroupMessageOccurence = (
  collections: CollectionsType,
  currentPage: PageRecordType | null,
  liveComponentMap: Component.ComponentMap,
  stagedComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) => {
  const liveMessageOccurenceCount = groupResults(
    validateComponentMap(collections, currentPage, liveComponentMap, plugins)
  );

  const stagedMessageOccurenceCount = groupResults(
    validateComponentMap(collections, currentPage, stagedComponentMap, plugins)
  );

  return {liveMessageOccurenceCount, stagedMessageOccurenceCount};
};

export const validateComponentMapConstraintsWithAllErrors = (
  collections: CollectionsType,
  currentPage: PageRecordType | null,
  liveComponentMap: Component.ComponentMap,
  stagedComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
): Result<
  Array<[string, Array<DesignerErrorCodes.ErrorCode>]>,
  Component.ComponentMap
> => {
  const {liveMessageOccurenceCount, stagedMessageOccurenceCount} =
    validateComponentMapsAndGroupMessageOccurence(
      collections,
      currentPage,
      liveComponentMap,
      stagedComponentMap,
      plugins
    );

  const errorsIntroduced = entries(stagedMessageOccurenceCount).reduce<
    Array<[string, Array<DesignerErrorCodes.ErrorCode>]>
  >((result, [stagedMessage, errorMappings]) => {
    const errorWasIntroduced = liveMessageOccurenceCount[stagedMessage] == null;

    if (errorWasIntroduced) {
      result.push([stagedMessage, errorMappings]);
    }

    return result;
  }, []);

  if (errorsIntroduced.length > 0) {
    return Err(errorsIntroduced);
  }

  return Ok(stagedComponentMap);
};

export const validateComponentMapConstraints = (
  collections: CollectionsType,
  currentPage: PageRecordType | null,
  liveComponentMap: Component.ComponentMap,
  stagedComponentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType
) =>
  either<
    Array<[string, Array<DesignerErrorCodes.ErrorCode>]>,
    Result<DesignerErrorCodes.ErrorCode, Component.ComponentMap>
  >((errors) => {
    const [, [firstErrorMapping]] = errors![0]!;
    return Err(firstErrorMapping!);
  })(Ok<Component.ComponentMap>)(
    validateComponentMapConstraintsWithAllErrors(
      collections,
      currentPage,
      liveComponentMap,
      stagedComponentMap,
      plugins
    )
  );

/**
 * Compares a live and staged raw page/component definition without traversing
 * into component instances. Relationship constraints are local to this
 * component definition.
 */
export const validateComponentDefinitionConstraintChanges = (
  collections: CollectionsType,
  currentPage: PageRecordType,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  currentComponentType: Plugins.QualifiedElement,
  currentComponent: Component.ComponentType,
  liveExpression: Expression,
  stagedExpression: Expression
): Result<DesignerErrorCodes.ErrorCode, Expression> => {
  const liveMessageOccurenceCount = groupResults(
    validateComponentDefinitionConstraints(
      collections,
      currentPage,
      componentMap,
      plugins,
      currentComponent,
      liveExpression
    )
  );
  const stagedCurrentComponent = Component.update(currentComponent, {
    render: stagedExpression,
  });
  const stagedComponentMap = componentMap.set(
    currentComponentType,
    stagedCurrentComponent
  );
  const stagedMessageOccurenceCount = groupResults(
    validateComponentDefinitionConstraints(
      collections,
      currentPage,
      stagedComponentMap,
      plugins,
      stagedCurrentComponent,
      stagedExpression
    )
  );

  const firstIntroducedError = entries(stagedMessageOccurenceCount).find(
    ([stagedMessage]) => liveMessageOccurenceCount[stagedMessage] == null
  );

  if (firstIntroducedError) {
    const [, [firstErrorMapping]] = firstIntroducedError;
    return Err(firstErrorMapping!);
  }

  return Ok(stagedExpression);
};

/**
 * `isPinnedToParent` is used by the Designer for proxying element behaviors.
 * For example, when you remove a Collection List, the operation is proxied
 * to the Collection List Wrapper because it `isPinnedToParent`.
 */
export const isPinnedToParent = (
  element: ExpressionElement,
  plugins: Plugins.PluginsType
): boolean => {
  if (isStringElement(element)) {
    return false;
  }

  // User component instances (symbols) are not pinned to any parent
  if (getPluginName(element.type) === '__SitePlugin') {
    return false;
  }

  return Plugins.getAtomByType(plugins, getName(element.type)).constraints
    .pinToParent;
};
